import CloudIconImpactPill from "@/components/icons/CloudIconImpactPill";
import LeafIconImpactPill from "@/components/icons/LeafIconImpactPill";
import PremiumBannerTooltip from "@/components/PremiumFeatureComponents/PremiumBannerTooltip";
import useImpactMethod from "@/hooks/useImpactMethod";
import useTranslation from "@/hooks/useTranslation";
import YearPicker from "@/pages/dashboard/components/YearPicker/YearPicker";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import {
  convertMass,
  Diff,
  formatCarbonFootprintDiff,
  formatEnvironmentalCost,
  formatEnvironmentalCostDiff,
  formatPercentageDiff,
} from "@/pages/dashboard/utils/formatters";
import { IImpact } from "@/pages/dashboard/utils/types";
import Icon, {
  ExclamationCircleOutlined,
  HourglassOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { ReactNode } from "react";
import Skeleton from "../Skeleton/Skeleton";
import styles from "./ImpactPill.module.scss";

export type ImpactPillType = "default" | "borderless";

export type CompareMode = "absolute" | "percentage";

export default function ImpactPill({
  impact,
  compareImpact,
  carbonRenderer,
  environmentalRenderer,
  before,
  carbonTooltip,
  environmentalTooltip,
  loading,
  showDate,
  error,
  errorTooltip,
  incomplete,
  incompleteTooltip,
  calculating,
  type = "default",
  compareMode = "absolute",
  blur = false,
}: {
  impact?: IImpact;
  compareImpact?: IImpact;
  carbonRenderer?: (children: ReactNode) => ReactNode;
  environmentalRenderer?: (children: ReactNode) => ReactNode;
  before?: ReactNode;
  carbonTooltip?: string;
  environmentalTooltip?: string;
  loading?: boolean;
  showDate?: boolean;
  error?: boolean;
  errorTooltip?: string;
  incomplete?: boolean;
  incompleteTooltip?: string;
  calculating?: boolean;
  type?: ImpactPillType;
  compareMode?: CompareMode;
  blur?: boolean;
}) {
  const { year } = useDashboardYear();
  const translation = useTranslation();
  const { environmentalCostMetric } = useImpactMethod();

  environmentalTooltip = environmentalTooltip || translation.general.environmentalCostTooltip;
  carbonTooltip = carbonTooltip || translation.general.carbonFootprintTooltip;

  if (!carbonRenderer) carbonRenderer = (children: ReactNode) => children;
  if (!environmentalRenderer) environmentalRenderer = (children: ReactNode) => children;

  const convertCarbon =
    impact?.carbon_footprint !== undefined ? convertMass(impact?.carbon_footprint) : undefined;

  const convertEnvironmental =
    impact?.[environmentalCostMetric] !== undefined
      ? formatEnvironmentalCost(impact?.[environmentalCostMetric], true)
      : undefined;

  const diffCarbon =
    impact && compareImpact
      ? compareMode === "absolute"
        ? formatCarbonFootprintDiff(impact?.carbon_footprint - compareImpact?.carbon_footprint)
        : formatPercentageDiff(impact?.carbon_footprint, compareImpact?.carbon_footprint)
      : undefined;

  const diffEnvironmental =
    impact && compareImpact
      ? compareMode === "absolute"
        ? formatEnvironmentalCostDiff(
            impact?.[environmentalCostMetric] - compareImpact?.[environmentalCostMetric]
          )
        : formatPercentageDiff(
            impact?.[environmentalCostMetric],
            compareImpact?.[environmentalCostMetric]
          )
      : undefined;

  return (
    <div className={`${styles.Impact} ${styles[type]}`}>
      {before && (
        <span className={styles.child}>
          {loading ? <Skeleton style={{ width: 40, height: 14 }} /> : before}
        </span>
      )}
      {showDate && year && (
        <span className={`${styles.date} ${styles.child}`}>
          <YearPicker
            size="small"
            variant="borderless"
          />
        </span>
      )}
      {carbonRenderer(
        <PremiumBannerTooltip show={blur}>
          <span className={`${styles.carbon} ${styles.child} ${blur ? styles.blur : ""}`}>
            <Tooltip
              placement="bottom"
              title={!blur && carbonTooltip}
            >
              <Icon
                component={CloudIconImpactPill}
                className={styles.impact_icon}
              />
            </Tooltip>

            {loading ? (
              <Skeleton style={{ width: 40, height: 14 }} />
            ) : blur ? (
              <>
                <span className={styles.number}>123.2</span>
                <span className={styles.unit}>g</span>
              </>
            ) : convertCarbon && !calculating ? (
              <>
                <span className={styles.number}>{convertCarbon.formattedNumber}</span>
                <span className={styles.unit}>{convertCarbon.unit}</span>
                <Difference diff={diffCarbon} />
              </>
            ) : (
              "--"
            )}
          </span>
        </PremiumBannerTooltip>
      )}
      {environmentalRenderer(
        <PremiumBannerTooltip show={blur}>
          <span className={`${styles.environmental} ${styles.child} ${blur ? styles.blur : ""}`}>
            <Tooltip
              placement="bottom"
              title={!blur && environmentalTooltip}
            >
              <Icon
                component={LeafIconImpactPill}
                className={styles.impact_icon}
              />
            </Tooltip>
            {loading ? (
              <Skeleton style={{ width: 40, height: 14 }} />
            ) : blur ? (
              <>
                <span className={styles.number}>345.2</span>
                <span className={styles.unit}>g</span>
              </>
            ) : convertEnvironmental && !calculating ? (
              <>
                <span className={styles.unit}>&euro;</span>
                <span>{convertEnvironmental}</span>
                <Difference diff={diffEnvironmental} />
              </>
            ) : (
              "--"
            )}
          </span>
        </PremiumBannerTooltip>
      )}
      {error && (
        <span className={`${styles.error} ${styles.child}`}>
          <Tooltip
            placement="bottom"
            title={errorTooltip}
          >
            <WarningOutlined />
          </Tooltip>
        </span>
      )}
      {incomplete && (
        <span className={`${styles.incomplete} ${styles.child}`}>
          <Tooltip
            placement="bottom"
            title={incompleteTooltip}
          >
            <ExclamationCircleOutlined />
          </Tooltip>
        </span>
      )}
      {calculating && (
        <span className={`${styles.calculating} ${styles.child}`}>
          <Tooltip
            placement="bottom"
            title={translation.general.calculatingImpactTooltip}
          >
            <HourglassOutlined />
          </Tooltip>
        </span>
      )}
    </div>
  );
}

export function Difference({ diff }: { diff?: Diff }) {
  if (!diff) return;
  return <span className={`${styles.diff} ${styles[diff.sign]}`}>{diff.formattedNumber}</span>;
}
